import React, {useEffect, useCallback, useState, useMemo} from 'react';
import styled from 'styled-components';

import {useApi} from '../../lib/api';
import useAsync from '../../lib/use-async';
import search from '../../lib/search';
import SideModal from '../../components/SideModal';
import Loading from '../../components/Loading';
import QuestionSelectorTree from '../../components/QuestionSelectorTree';
import Input from '../../components/Input';
import Button from '../../components/Button';
import Error from '../../components/Error';

function ImportQuestionModal({ open, onClose, onImport }) {
  const api = useApi();
  const [getTemplates, {loading, error, result: forms}] = useAsync(api.getCompanyTemplates);
  const [textFilter, setTextFilter] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    getTemplates();
  }, []);

  const handleSelectQuestion = useCallback(question => {
    setSelected(question);
  }, [setSelected]);

  const filteredForms = useMemo(
    () => {
      if (!forms) {
        return null;
      }

      return forms.filter(form => {
        if (textFilter === '') {
          return true;
        }

        const matchTitle = search(form.name, textFilter);
        const matchAnyQuestion = [...form.questions, ...form.contextQuestions]
          .filter(question => search(question.text, textFilter))
          .length > 0;
        
        return matchTitle || matchAnyQuestion;
      }).map(form => {
        if (textFilter === '') {
          return form;
        }

        return {
          ...form,
          contextQuestions: form.contextQuestions.filter(question => search(question.text, textFilter)),
          questions: form.questions.filter(question => search(question.text, textFilter))
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name));
    },
    [forms, textFilter]
  );

  return (
    <SideModal 
      open={open}
      large
      title="Import Question"
      onClose={onClose}
      options={[
        <Button primary disabled={!selected} onClick={() => onImport(selected)}>Import Question</Button>,
        <Button secondary onClick={onClose}>Cancel</Button>
      ]}>
      <SideModal.Content>
        {loading && <Loading />}
        {error && <Error message="Unable to load questions!" />}
        {filteredForms && (
          <>
            <FilterOptions>
              <Input placeholder="Filter forms and questions..." value={textFilter} onChange={e => setTextFilter(e.target.value)} />
            </FilterOptions>
            <QuestionSelectorTree
              expandAll={textFilter !== ''}
              selected={selected}
              forms={filteredForms}
              onSelect={handleSelectQuestion}
            />
          </>
        )}
      </SideModal.Content>
    </SideModal>
  );
}

const FilterOptions = styled.div`
  margin-bottom: 1rem;
`;

export default ImportQuestionModal;